/**
 * @description 满量特价/满量折扣阶梯文案
 * - 满量折扣(type=2)：discountVO.discountItems，输出折扣比例
 * - 满量特价(type=1)：volumePriceVO.hierarchies，输出特价金额
 */

import { isStorePromotionLineStatusAvailable } from '~/utils/product'
import { isVolumeDiscount, isVolumePrice, PROMOTION_TYPE } from './promotionTypes'

const EPS = 0.005

/**
 * @description 阶梯项展示文案：按 `discountVO.discountRule` 固定模板拼接；字段不足时回退 `discountItemDesc`
 * @param {Record<string, unknown>} it - discountItems 单项
 * @param {number} rule - discountRule：0 满钱减钱 1 满件减钱 2 满钱打折 3 满件打折
 * @returns {string}
 */
function formatDiscountTierLine(it, rule) {
  const needRaw = it?.needAmount
  const needText = formatNeedOrDiscountNum(needRaw, { maxDecimals: 2 })
  let synthesized = ''

  if (needText) {
    if (rule === 0) {
      const amt = formatNeedOrDiscountNum(it?.discount, { maxDecimals: 2 })
      if (amt) synthesized = `满${needText}元减${amt}元`
    } else if (rule === 1) {
      const amt = formatNeedOrDiscountNum(it?.discount, { maxDecimals: 2 })
      if (amt) synthesized = `满${needText}台减${amt}元`
    } else if (rule === 2) {
      const zhe = resolveZheDisplay(it)
      if (zhe) synthesized = `满${needText}元享${zhe}折`
    } else if (rule === 3) {
      const zhe = resolveZheDisplay(it)
      if (zhe) synthesized = `满${needText}台享${zhe}折`
    }
  }

  if (synthesized) return synthesized
  return String(it?.discountItemDesc ?? '').trim()
}

/**
 * @param {*} value
 * @param {{ maxDecimals?: number }} [opts]
 * @returns {string}
 */
function formatNeedOrDiscountNum(value, { maxDecimals = 2 } = {}) {
  if (value === null || value === undefined || value === '') return ''
  if (typeof value === 'number' && Number.isFinite(value)) {
    if (Number.isInteger(value)) return String(value)
    return String(Number(value.toFixed(maxDecimals)))
  }
  const raw = String(value).trim()
  if (!raw) return ''
  const parsed = Number(raw.replace(/[^\d.]/g, ''))
  if (!Number.isFinite(parsed)) return raw
  if (Number.isInteger(parsed)) return String(parsed)
  return String(Number(parsed.toFixed(maxDecimals)))
}

/**
 * @param {Record<string, unknown>} it
 * @returns {string}
 */
function resolveZheDisplay(it) {
  const raw = it?.discount ?? it?.discountValue ?? it?.couponDiscount ?? ''
  const num = Number(String(raw).replace(/[^\d.]/g, ''))
  if (!Number.isFinite(num)) return ''
  let normalized = num
  if (normalized > 0 && normalized <= 1) normalized *= 10
  if (normalized > 10 && normalized <= 100) normalized /= 10
  return formatNeedOrDiscountNum(normalized, { maxDecimals: 1 })
}

/**
 * @param {Array<Record<string, unknown>>} items
 * @returns {Array<Record<string, unknown>>}
 */
function sortDiscountItemsByNeed(items) {
  return [...items].sort((a, b) => {
    const na = Number(a?.needAmount)
    const nb = Number(b?.needAmount)
    return (Number.isFinite(na) ? na : 0) - (Number.isFinite(nb) ? nb : 0)
  })
}

/**
 * @description 满钱 / 满元享折（`discountRule` 0、2）：用该行「原价小计」与阶梯 `needAmount`（元）比较；优先接口 `totalAmount`/`totalPrice`，其次 `originalUnitPrice×数量`，再 `实付+行 discountAmount`，避免误用折后小计导致下一档判断错误
 * @param {Record<string, unknown>} itemLike
 * @returns {number}
 */
function resolveMoneyMarkingProgressValue(itemLike = {}) {
  const ta = Number(itemLike.totalAmount ?? itemLike.total_amount)
  if (Number.isFinite(ta) && ta > EPS) return ta

  const tp = Number(itemLike.totalPrice ?? itemLike.total_price)
  if (Number.isFinite(tp) && tp > EPS) return tp

  const q = Number(
    itemLike.quantity ?? itemLike.count ?? itemLike.prodCount ?? itemLike.basketCount ?? 0
  )
  const ou = Number(itemLike.originalUnitPrice ?? itemLike.originalPrice)
  if (Number.isFinite(ou) && ou > EPS && Number.isFinite(q) && q > 0) return ou * q

  const b = Number(itemLike.baseAmount ?? itemLike.base_amount)
  if (Number.isFinite(b) && b > EPS) return b

  const sub = Number(
    itemLike.subtotal ?? itemLike.actualTotal ?? itemLike.actual_total ?? itemLike.finalAmount ?? 0
  )
  const da = Number(itemLike.discountAmount ?? itemLike.discount_amount ?? 0)
  if (Number.isFinite(sub) && Number.isFinite(da) && da > EPS) return sub + da
  return Number.isFinite(sub) ? sub : 0
}

/**
 * @description 解析满减/满件进度：`discountRule` 1、3 用件数；0、2 用原价小计（元）
 * @param {Record<string, unknown>} itemLike
 * @param {number} rule
 * @returns {number}
 */
/**
 * @description 限时折扣/满减活动分组键：同活动 + 同商品（prodId）多规格合并计档；不同商品不联动
 * @param {Record<string, unknown>} line - V3 shopCartItem 或扁平行
 * @returns {string} 空串表示非 promotionType=2
 */
export function resolveMarkingDiscountActivityKey(line = {}) {
  if (!line || typeof line !== 'object') return ''

  const pt = Number(line.promotionType)
  // 支持满量折扣(2)和满量特价(1)
  if (!isVolumeDiscount(pt) && !isVolumePrice(pt)) return ''

  const promotionId =
    line.discountVO?.discountId ?? line.volumePriceVO?.promotionId ?? line.promotionId ?? line.promotionCode
  if (promotionId == null || promotionId === '') return ''

  const pv = line.productVO && typeof line.productVO === 'object' ? line.productVO : {}
  const prodId = line.prodId ?? line.productId ?? pv.prodId
  if (prodId != null && prodId !== '') {
    return `${pt}:${promotionId}:${prodId}`
  }

  const basketId = line.basketId ?? line.id
  if (basketId != null && basketId !== '') {
    return `${pt}:${promotionId}:b:${basketId}`
  }

  return `${pt}:${promotionId}`
}

/**
 * @description 从 V3 购物车行构建阶梯进度快照（与 `mapV3ShopCartLineToFlatItem` 口径一致）
 * @param {Record<string, unknown>} line
 * @returns {Record<string, unknown>}
 */
/**
 * @description 购物车满减联动进度（元）：优先「原价单价×件数」，与改数量草稿缩放口径一致，避免接口刷新后 baseAmount 未同步导致标签回退
 * @param {Record<string, unknown>} line - V3 行或扁平行
 * @param {number} [rule=0] - discountRule
 * @returns {number}
 */
export function resolveCartMarkingProgressForLine(line = {}, rule = 0) {
  if (!line || typeof line !== 'object') return 0

  const r = Number(rule)
  const qty = Math.max(
    1,
    Number(line.basketCount ?? line.prodCount ?? line.quantity ?? line.count ?? 1) || 1
  )

  if (r === 1 || r === 3) {
    return qty
  }

  const storeQty = Math.max(
    1,
    Number(line.basketCount ?? line.prodCount ?? line.quantity ?? line.count ?? 1) || 1
  )

  let ou = Number(line.originalUnitPrice ?? line.originalPrice ?? line.original_unit_price)
  if (!Number.isFinite(ou) || ou <= EPS) {
    const base = Number(line.baseAmount ?? line.base_amount)
    if (Number.isFinite(base) && base > EPS && storeQty > 0) {
      ou = base / storeQty
    }
  }
  if (!Number.isFinite(ou) || ou <= EPS) {
    const pv = line.productVO && typeof line.productVO === 'object' ? line.productVO : {}
    const sv = line.skuVO && typeof line.skuVO === 'object' ? line.skuVO : {}
    ou = Number(
      sv.oriPrice ?? sv.originalPrice ?? pv.originalPrice ?? pv.price ?? sv.price ?? sv.smbPrice
    )
  }
  if (Number.isFinite(ou) && ou > EPS) {
    return Math.round(ou * qty * 100) / 100
  }

  const like = buildMarkingProgressLikeFromV3CartLine(line)
  return resolveMarkingProgressValue(like, r)
}

export function buildMarkingProgressLikeFromV3CartLine(line = {}) {
  if (!line || typeof line !== 'object') return {}

  const qty = Math.max(1, Number(line.basketCount ?? line.prodCount ?? 1) || 1)
  const finalAmt = Number(line.finalAmount)
  const activityAmt = Number(line.activityAmount)
  const baseAmt = Number(line.baseAmount)
  const lineTotalAmt = Number(line.totalAmount ?? line.totalPrice ?? 0)
  const lineDiscAmt = Number(line.discountAmount ?? 0)
  const sub = Number.isFinite(finalAmt) && finalAmt > 0 ? finalAmt : activityAmt
  const moneyProgress =
    Number.isFinite(baseAmt) && baseAmt > EPS
      ? baseAmt
      : lineTotalAmt > EPS
        ? lineTotalAmt
        : Number.isFinite(sub) && lineDiscAmt > EPS
          ? sub + lineDiscAmt
          : sub
  return {
    totalAmount: moneyProgress,
    totalPrice: lineTotalAmt,
    baseAmount: baseAmt,
    discountAmount: lineDiscAmt,
    subtotal: sub,
    actualTotal: sub,
    finalAmount: finalAmt,
    quantity: qty,
    count: qty,
    prodCount: qty,
    basketCount: qty,
  }
}

/**
 * @description 同活动多行汇总后的进度 → `resolveCartLineNextMarkingHintText` 入参
 * @param {number} progress - 组内汇总进度（元或件）
 * @param {number} rule - discountRule
 * @returns {Record<string, unknown>}
 */
export function buildCtxForAggregatedMarkingProgress(progress, rule) {
  const r = Number(rule)
  if (r === 1 || r === 3) {
    return { promotionType: 2, quantity: progress }
  }
  return {
    promotionType: 2,
    totalAmount: progress,
    baseAmount: progress,
    subtotal: progress,
    actualTotal: progress,
    finalAmount: progress,
    quantity: 1,
  }
}

export function resolveMarkingProgressValue(itemLike = {}, rule) {
  if (!itemLike || typeof itemLike !== 'object') return 0

  const r = Number(rule)
  if (r === 1 || r === 3) {
    const q = Number(
      itemLike.quantity ?? itemLike.count ?? itemLike.prodCount ?? itemLike.basketCount ?? 0
    )
    return Number.isFinite(q) ? q : 0
  }
  return resolveMoneyMarkingProgressValue(itemLike)
}

/**
 * @description 订单/售后扁平行 → 满减进度快照（与 V3 购物车行口径对齐）
 * @param {Record<string, unknown>} line
 * @returns {Record<string, unknown>}
 */
export function buildMarkingProgressLikeFromOrderLine(line = {}) {
  if (!line || typeof line !== 'object') return {}

  const qty = Math.max(
    1,
    Number(line.quantity ?? line.count ?? line.prodCount ?? line.basketCount ?? 1) || 1
  )
  const sub = Number(
    line.subtotal ?? line.actualTotal ?? line.actual_total ?? line.finalAmount ?? 0
  )
  return {
    totalAmount: line.totalAmount ?? line.total_amount,
    totalPrice: line.totalPrice ?? line.total_price,
    baseAmount: line.baseAmount ?? line.base_amount,
    originalUnitPrice: line.originalUnitPrice ?? line.original_unit_price,
    discountAmount: line.discountAmount ?? line.discount_amount,
    subtotal: sub,
    actualTotal: sub,
    finalAmount: line.finalAmount ?? line.final_amount,
    quantity: qty,
    count: qty,
    prodCount: qty,
    basketCount: qty,
  }
}

/**
 * @description 按汇总进度解析已达成阶梯中的最高一档文案
 * @param {Record<string, unknown>} dto - discountVO
 * @param {number} progress - 组内汇总进度（元或件）
 * @param {number} [rule] - discountRule，缺省取 dto.discountRule
 * @returns {string}
 */
export function resolveOrderAppliedMarkingHintFromDto(dto, progress, rule) {
  if (!dto || typeof dto !== 'object') return ''

  const items = Array.isArray(dto.discountItems) ? dto.discountItems : []
  const r = Number(rule ?? dto.discountRule)
  if (!items.length) {
    return String(dto.discountShortName ?? dto.discountName ?? '').trim()
  }

  const sorted = sortDiscountItemsByNeed(items)
  let bestLine = ''
  for (const it of sorted) {
    const need = Number(it?.needAmount)
    if (!Number.isFinite(need)) continue
    if (progress + EPS < need) continue
    const line = formatDiscountTierLine(it, r)
    if (line) bestLine = line
  }
  return bestLine
}

/**
 * @description 限时折扣订单行按「活动 + 商品」汇总进度 → 已享受的最高一档标签（同商品多规格合并）
 * @param {Array<Record<string, unknown>>} lines
 * @returns {Map<string, string>} activityKey → applied hint
 */
export function buildLinkedOrderLineAppliedMarkingTagMap(lines = []) {
  const items = Array.isArray(lines) ? lines : []
  const groups = new Map()

  for (const line of items) {
    if (!line || typeof line !== 'object') continue
    const pt = Number(line.promotionType) || 0
    if (pt > 0 && !isStorePromotionLineStatusAvailable(line)) continue
    const key = resolveMarkingDiscountActivityKey(line)
    if (!key) continue

    if (!groups.has(key)) {
      const dto = line.discountVO
      groups.set(key, {
        dto: dto && typeof dto === 'object' ? dto : {},
        rule: Number(dto?.discountRule),
        lines: [],
      })
    }
    groups.get(key).lines.push(line)
  }

  const tagByKey = new Map()
  for (const [key, group] of groups) {
    let totalProgress = 0
    for (const line of group.lines) {
      const like = buildMarkingProgressLikeFromOrderLine(line)
      totalProgress += resolveMarkingProgressValue(like, group.rule)
    }
    const hint = resolveOrderAppliedMarkingHintFromDto(group.dto, totalProgress, group.rule)
    if (hint) tagByKey.set(key, hint)
  }
  return tagByKey
}

/**
 * @description 为各行写入 `linkedAppliedMarkingHint`（同分组键统一已享受标签，默认每行一键）
 * @param {Array<Record<string, unknown>>} lines
 * @returns {Array<Record<string, unknown>>}
 */
export function applyLinkedOrderLineAppliedMarkingTags(lines = []) {
  const items = Array.isArray(lines) ? lines : []
  const map = buildLinkedOrderLineAppliedMarkingTagMap(items)
  if (!map.size) return items

  return items.map((line) => {
    if (!line || typeof line !== 'object') return line

    const pt = Number(line.promotionType) || 0
    if (pt > 0 && !isStorePromotionLineStatusAvailable(line)) return line
    const key = resolveMarkingDiscountActivityKey(line)
    if (!key || !map.has(key)) return line
    const tag = map.get(key)
    if (!tag) return line
    return { ...line, linkedAppliedMarkingHint: tag }
  })
}

/**
 * @description 订单确认 / 订单详情 / 售后：在已达成阶梯中仅展示「最高一档」规则模板；有 `discountItems` 时不再回退冗长 `discountLabel`
 * @param {Record<string, unknown>} item
 * @returns {string}
 */
export function resolveOrderLineAppliedMarkingHintText(item = {}) {
  if (!item || typeof item !== 'object') return ''

  if (Number(item.promotionType) !== 2) return ''
  if (!isStorePromotionLineStatusAvailable(item)) return ''

  const linked = String(item.linkedAppliedMarkingHint ?? '').trim()
  if (linked) return linked

  const dto = item.discountVO
  if (!dto || typeof dto !== 'object') return ''

  const rule = Number(dto.discountRule)
  const progress = resolveMarkingProgressValue(item, rule)
  return resolveOrderAppliedMarkingHintFromDto(dto, progress, rule)
}

/**
 * @description 购物车：未达下一档时展示下一阶梯规则模板；已满最高档时展示已享受的最高一档（与 App 一致）
 * @param {Record<string, unknown>} dto - 合并后的活动 DTO（须保留 discountItems）
 * @param {{ promotionType: number, baseAmount?: number, quantity: number, totalAmount?: number, originalUnitPrice?: number, discountAmount?: number, subtotal?: number, actualTotal?: number, finalAmount?: number }} ctx
 * @returns {string}
 */
export function resolveCartLineNextMarkingHintText(dto, ctx = {}) {
  if (!dto || typeof dto !== 'object') return ''
  if (!ctx || typeof ctx !== 'object') return ''

  const pt = Number(ctx.promotionType)
  if (pt !== 2) return ''

  const items = Array.isArray(dto.discountItems) ? dto.discountItems : []
  const rule = Number(dto.discountRule)
  if (!items.length) {
    const name = typeof dto.discountName === 'string' ? dto.discountName.trim() : ''
    return name
  }

  const progressLike = {
    totalAmount: ctx.totalAmount,
    baseAmount: ctx.baseAmount,
    originalUnitPrice: ctx.originalUnitPrice,
    discountAmount: ctx.discountAmount,
    subtotal: ctx.subtotal,
    actualTotal: ctx.actualTotal,
    finalAmount: ctx.finalAmount,
    quantity: ctx.quantity,
    count: ctx.quantity,
    prodCount: ctx.quantity,
    basketCount: ctx.quantity,
  }
  const progress = resolveMarkingProgressValue(progressLike, rule)
  const sorted = sortDiscountItemsByNeed(items)
  const next = sorted.find((it) => {
    const need = Number(it?.needAmount)
    return Number.isFinite(need) && progress + EPS < need
  })

  if (next) {
    const tierTxt = formatDiscountTierLine(next, rule)
    if (tierTxt) return tierTxt
    return typeof dto.discountName === 'string' ? dto.discountName.trim() : ''
  }

  const last = sorted[sorted.length - 1]
  const lastNeed = Number(last?.needAmount)
  if (last && Number.isFinite(lastNeed) && progress + EPS >= lastNeed) {
    const applied = resolveOrderAppliedMarkingHintFromDto(dto, progress, rule)
    if (applied) return applied
    const lastTierTxt = formatDiscountTierLine(last, rule)
    if (lastTierTxt) return lastTierTxt
  }

  return typeof dto.discountName === 'string' ? dto.discountName.trim() : ''
}
